<template>
  <header
    class="z-40 w-full flex flex-col border-t-3 border-brand-600 relative"
    :class="{ 'mobile-menu-open': mobileMenuOpen }"
    :style="{ backgroundColor: backgroundColor ?? 'white' }"
  >
    <div class="z-30 w-full md:h-full xl:h-24 md:overflow-y-visible shadow-2 xl:shadow-0">
      <div
        class="flex w-full xl:h-full justify-between items-center max-w-screen-2xl mx-auto px-5 py-4 md:p-5 xl:pb-0 xl:px-14 xl:pt-2"
      >
        <a class="h-8 md:h-11 mr-7" :href="logoUrl">
          <MImage
            class="h-full object-contain"
            src="/images/logo.svg"
            :alt="`${siteName} logo`"
            :lazy="false"
          />
        </a>
        <DesktopMenu v-if="navigation" :navigation="navigation" />
        <div class="flex justify-end">
          <div v-if="!noSearch && !searchError" class="mr-1 hidden md:block">
            <button
              v-if="!searchInitialized"
              class="w-12 h-full flex justify-center items-center rounded-6 hover:bg-gray-50 focus:bg-gray-50"
              @click="openSearch"
            >
              <svg role="presentation" focusable="false" tabindex="-1" class="icon size-5"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M16.04 15.86a1 1 0 0 0-.18.18 6.97 6.97 0 0 1-9.81-.09 6.98 6.98 0 0 1 0-9.9 6.98 6.98 0 0 1 9.9 0 6.98 6.98 0 0 1 .1 9.8zm5.67 4.43-3.68-3.67a8.97 8.97 0 0 0-.67-11.98 8.97 8.97 0 0 0-12.72 0 8.97 8.97 0 0 0 0 12.72 8.97 8.97 0 0 0 11.98.67l3.67 3.68a1 1 0 1 0 1.42-1.42z"/></svg>
            </button>
            <LazyIfMSearch
              v-show="searchInitialized"
              @over-search="overSearch"
              @click-search="clickSearch"
              @leave-search="leaveSearch"
              :hydrate-when="searchInitialized"
            />
          </div>
          <a
            v-if="!noAppButton"
            v-show="link"
            class="md:hidden"
            :href="link"
            target="_blank"
            rel="nofollow noopener"
            @click="trackClick"
          >
            <div class="flex bg-blue-800 rounded-12 p-2 items-center">
              <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-md"   alt="download" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><title>download</title><path fill="#fff" d="m12 15.8-4.3-4.3 1-1 2.6 2.4V4.5h1.4v8.4l2.5-2.5 1 1.1-4.2 4.3Zm-5.7 3.7c-.5 0-1-.2-1.3-.5a1.7 1.7 0 0 1-.5-1.3V15H6v2.7l.1.2a.3.3 0 0 0 .2.1h11.4a.3.3 0 0 0 .2-.1.3.3 0 0 0 .1-.2V15h1.5v2.7c0 .5-.2 1-.5 1.3-.4.3-.8.5-1.3.5H6.3Z"/></svg>
              <p class="text-white text-14 font-normal ml-1">{{ $t('app') }}</p>
            </div>
          </a>
          <LanguageSelector v-if="!noLanguageSelector" :alternate-languages="alternateLanguages" />
          <DropdownMenu v-if="navigation" :navigation="navigation" />
          <button
            v-if="navigation"
            class="btn btn-xs -mr-4 md:hidden focus:bg-transparent hover:bg-transparent"
            @click="toggleMobileMenu"
          >
            <BurgerToggleIcon :is-open="mobileMenuOpen" />
          </button>
        </div>
      </div>
    </div>
    <MobileMenu
      v-if="mobileMenuOpen && navigation"
      :navigation="navigation"
      :alternate-languages="alternateLanguages"
      :no-search="noSearch"
      @over-search="overSearch"
      @click-search="openSearch"
      @leave-search="leaveSearch"
    />
  </header>
</template>

<script lang="ts" setup>
import LanguageSelector from './language-selector.vue'
import MobileMenu from './mobile-menu.vue'
import DesktopMenu from './desktop-menu.vue'
import DropdownMenu from './dropdown-menu.vue'
import BurgerToggleIcon from './burger-toggle-icon.vue'
import type { AlternateLanguage } from './types'
import type { GQLNavigationItem } from './generated/page-header'
import {
  NavigationMenuOpen,
  HeaderSearchIconClick,
  HeaderSearchIconHover,
} from '@core/analytics/core-events/navigation'
import { AppPromptClick } from '@core/analytics/core-events/app'

const props = defineProps<{
  navigation: GQLNavigationItem[] | null
  alternateLanguages: AlternateLanguage[]
  vertical?: string
  noSearch?: boolean
  noAppButton?: boolean
  noLanguageSelector?: boolean
  backgroundColor?: string
}>()

const DELAY_PERIOD = 500

const { $track, $lang, $t } = useNuxtApp()

const link = useAppLink('header_button', props.vertical)

const mobileMenuOpen = ref(false)
async function toggleMobileMenu() {
  document.body.classList.toggle('overflow-hidden', !mobileMenuOpen.value)
  mobileMenuOpen.value = !mobileMenuOpen.value
  if (mobileMenuOpen.value) {
    await $track(NavigationMenuOpen('Menu'))
  }
}

const { siteUrl } = useRuntimeConfig().public
const siteName = computed(() => siteUrl.replace('https://www.', ''))

const logoUrl = computed(() => getHomeUrl())

const searchError = ref(false)

const algoliaConfig = computed(() => {
  const { id, key } = useRuntimeConfig().public.algolia
  return {
    id,
    key,
    lang: $lang,
    translations: {
      by: $t('by'),
      placeholder: $t('header.search_placeholder'),
    },
  }
})

const logger = useLogger('page-header')

const autocompleteApi = ref()
const searchInitialized = ref(false)
async function openSearch() {
  if (!searchInitialized.value) {
    try {
      const { initAlgolia } = await import('../m-search/algolia')
      autocompleteApi.value = initAlgolia(algoliaConfig.value)
      searchInitialized.value = true
    } catch (e) {
      logger.error(e)
      searchError.value = true
    }
  }
  await clickSearch()
  autocompleteApi.value?.setIsOpen(true)
}

async function clickSearch() {
  await $track(HeaderSearchIconClick())
}

async function trackClick() {
  await $track(AppPromptClick('header_button', props.vertical))
}

const timer = ref<ReturnType<typeof setTimeout> | null>(null)

function overSearch() {
  timer.value = setTimeout(() => {
    $track(HeaderSearchIconHover()).catch(() => {
      // do nothing
    })
  }, DELAY_PERIOD)
}

function leaveSearch() {
  if (timer.value) {
    clearTimeout(timer.value)
  }
}
</script>

<style scoped>
.mobile-menu-open {
  @apply fixed inset-y-0 overflow-y-scroll md:static md:overflow-y-visible;
  -webkit-overflow-scrolling: touch;
}

.mobile-menu-open:before {
  @apply fixed inset-0 bg-white md:hidden;
  content: '';
  top: 3px;
}
</style>
